/** Earned-game-currency vocabulary and pure rules. No payment ownership or simulation imports. */
export const FISHER_PORTRAIT_IDS = ['fisher-salt', 'fisher-reef', 'fisher-ember', 'fisher-moss'] as const;
export type FisherPortraitId = typeof FISHER_PORTRAIT_IDS[number];
export const FISHER_NAME_MAX_LENGTH = 32;
export const MAX_OWNED_COSMETICS = 512;
export interface FisherIdentity { readonly name: string; readonly portrait: FisherPortraitId; }
export const DEFAULT_FISHER_IDENTITY: FisherIdentity = { name: 'Fisher', portrait: 'fisher-salt' };
export type CosmeticSlot = 'lantern' | 'pennant';
export interface OutfitterItem { readonly id: string; readonly label: string; readonly cost: number; readonly slot: CosmeticSlot; }
export const OUTFITTER_ITEMS: readonly OutfitterItem[] = [
  { id: 'lantern-amber', label: 'Harbour Lantern', cost: 50, slot: 'lantern' },
  { id: 'lantern-green', label: 'Green-glass Lantern', cost: 150, slot: 'lantern' },
  { id: 'pennant-striped', label: 'Striped Stern Pennant', cost: 400, slot: 'pennant' },
];
export interface OutfitterState {
  /** Debits against lifetime earned sim.coins; never a second earnings counter. */
  readonly spentCoins: number;
  /** Unknown future IDs survive saves as opaque values, never current products. */
  readonly ownedCosmetics: readonly string[];
  readonly equippedCosmetics: Readonly<Record<CosmeticSlot, string | null>>;
}
export type TransactionResult = { readonly ok: true } | { readonly ok: false; readonly reason: string };
export type OutfitterChange = { readonly ok: true; readonly value: OutfitterState } | { readonly ok: false; readonly reason: string };
export interface OutfitterView {
  readonly earnedCoins: number;
  readonly spentCoins: number;
  readonly balance: number;
  readonly equippedCosmetics: OutfitterState['equippedCosmetics'];
  readonly items: readonly (OutfitterItem & { readonly owned: boolean; readonly equipped: boolean; readonly affordable: boolean })[];
}
export function outfitterItem(id: string): OutfitterItem | undefined { return OUTFITTER_ITEMS.find(item => item.id === id); }
/** Render only this build's known, owned fittings; opaque future save data stays untouched. */
export function currentBoatFittings(state: OutfitterState): readonly string[] {
  const visible: string[] = [];
  for (const slot of ['lantern', 'pennant'] as const) {
    const id = state.equippedCosmetics[slot];
    if (id !== null && state.ownedCosmetics.includes(id) && outfitterItem(id)?.slot === slot) visible.push(id);
  }
  return visible;
}
function amount(value: number): number { return Number.isFinite(value) ? Math.min(Number.MAX_SAFE_INTEGER, Math.max(0, Math.trunc(value))) : 0; }
export function outfitterView(earnedCoins: number, state: OutfitterState): OutfitterView {
  const earned = amount(earnedCoins); const spent = amount(state.spentCoins); const balance = Math.max(0, earned - spent);
  return { earnedCoins: earned, spentCoins: spent, balance, equippedCosmetics: state.equippedCosmetics,
    items: OUTFITTER_ITEMS.map(item => ({ ...item, owned: state.ownedCosmetics.includes(item.id), equipped: state.equippedCosmetics[item.slot] === item.id, affordable: balance >= item.cost })) };
}
export function buyOutfitterItem(earnedCoins: number, state: OutfitterState, id: string): OutfitterChange {
  const item = outfitterItem(id);
  if (item === undefined) return { ok: false, reason: 'unknown-cosmetic' };
  if (state.ownedCosmetics.includes(id)) return { ok: false, reason: 'already-owned' };
  if (state.ownedCosmetics.length >= MAX_OWNED_COSMETICS) return { ok: false, reason: 'outfitter-full' };
  const wallet = outfitterView(earnedCoins, state);
  if (wallet.balance < item.cost) return { ok: false, reason: 'insufficient-coins' };
  return { ok: true, value: { ...state, spentCoins: wallet.spentCoins + item.cost, ownedCosmetics: [...state.ownedCosmetics, id].sort() } };
}
export function equipOutfitterItem(state: OutfitterState, slot: CosmeticSlot, id: string | null): OutfitterChange {
  if (slot !== 'lantern' && slot !== 'pennant') return { ok: false, reason: 'unknown-slot' };
  if (id !== null) {
    const item = outfitterItem(id);
    if (item === undefined) return { ok: false, reason: 'unknown-cosmetic' };
    if (item.slot !== slot) return { ok: false, reason: 'wrong-slot' };
    if (!state.ownedCosmetics.includes(id)) return { ok: false, reason: 'not-owned' };
  }
  return { ok: true, value: { ...state, equippedCosmetics: { ...state.equippedCosmetics, [slot]: id } } };
}
const NAME_CONTROLS = /[\u0000-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g;
function member(value: unknown, key: string): unknown {
  try { return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Record<string, unknown>)[key] : undefined; } catch { return undefined; }
}
export function normaliseFisherIdentity(value: unknown): FisherIdentity {
  const rawName = member(value, 'name'); const rawPortrait = member(value, 'portrait');
  const name = typeof rawName === 'string' ? Array.from(rawName.slice(0, 4096).replace(NAME_CONTROLS, '').trim()).slice(0, FISHER_NAME_MAX_LENGTH).join('') : '';
  const portrait = FISHER_PORTRAIT_IDS.find(id => id === rawPortrait) ?? DEFAULT_FISHER_IDENTITY.portrait;
  return { name: name || DEFAULT_FISHER_IDENTITY.name, portrait };
}
export function validateFisherIdentity(value: unknown): TransactionResult {
  const name = member(value, 'name'); const portrait = member(value, 'portrait');
  if (typeof name !== 'string' || name.length > 4096 || name.replace(NAME_CONTROLS, '') !== name || name.trim().length === 0 || Array.from(name.trim()).length > FISHER_NAME_MAX_LENGTH) return { ok: false, reason: 'invalid-name' };
  if (!FISHER_PORTRAIT_IDS.some(id => id === portrait)) return { ok: false, reason: 'unknown-portrait' };
  return { ok: true };
}
